/**
 * OV-1180: which Sub-Category steps accept several picks, and which options must
 * stand alone. Served by SubmissionPreFormBuilder so neither list is hardcoded here —
 * the backend re-validates any combination on create regardless.
 */
export interface MultiSelectConfig {
  categories: string[];
  exclusive_sub_categories: Record<string, string[]>;
}

export interface FormData {
  categories: string[];
  subcategories: Record<string, string[]>;
  variants: Record<string, string[]>;
  context: string;
  multi_select?: MultiSelectConfig;
  accepts_more_sites?: boolean;
  blocked?: { message: string };
  owner?: {
    id: number;
    title: string;
    display_id?: string;
    status?: string;
    eap_status?: 'EAP' | 'Not EAP';
    /**
     * OV-1358: the Continuing Review sub-category this owner implies — a site,
     * a single-site study or a multi-site study each have exactly one valid CR
     * form. Derived server-side (SubmissionPreFormBuilder#owner_info).
     *
     * Absent when a study's type is unknown (common on transferred studies whose
     * template_variant was never filled) — the wizard then shows the step.
     */
    continuing_review_sub_category?: string;
  };
}

export interface WizardSelection {
  category?: string;
  /**
   * Always an array, even for single-select categories (where it holds at most one
   * entry), so every consumer reads one shape.
   */
  subCategory?: string[];
  /** Variant per sub-category — only the few sub-categories that have variants appear. */
  variant?: Record<string, string>;
}

export interface SelectedForm {
  id: string;
  form_identifier: string;
  category: string;
  sub_category: string;
  variant: string;
}

export enum WizardStepType {
  CATEGORY = 'category',
  SUBCATEGORY = 'subCategory',
  VARIANT = 'variant',
  REVIEW = 'review',
}

export const STEP_ORDER = [
  WizardStepType.CATEGORY,
  WizardStepType.SUBCATEGORY,
  WizardStepType.VARIANT,
  WizardStepType.REVIEW,
];

export const STEP_LABELS: Record<WizardStepType, string> = {
  [WizardStepType.CATEGORY]: 'Select Category',
  [WizardStepType.SUBCATEGORY]: 'Select Sub-Category',
  [WizardStepType.VARIANT]: 'Select Variant',
  [WizardStepType.REVIEW]: 'Review',
};
export interface WizardState {
  currentStep: WizardStepType;
  selection: WizardSelection;
  selectedForm: SelectedForm | null;
  isLoading: boolean;
  error: string | null;
}

export interface WizardStepData {
  currentStep: WizardStepType;
  title: string;
  options: string[] | null;
  /** True on a Sub-Category step whose category combines (OV-1180). */
  allowMultiple?: boolean;
  /** Currently-selected values for this step. */
  selected: string[];
  /**
   * Options that cannot be picked alongside the current selection — the exclusive
   * sub-categories, plus everything else once one of them is picked.
   */
  disabledOptions?: string[];
  /**
   * True when the options are disabled because the user has already picked an
   * exclusive one (Translation/Appeal), rather than because they are themselves
   * exclusive. The two cases need opposite explanations.
   */
  disabledByExclusiveSelection?: boolean;
  /**
   * On a variant step, the sub-category being asked about. Option A of OV-1180 asks
   * the variant question once per selected sub-category that has variants, so the
   * step needs to say which one it means.
   */
  variantForSubCategory?: string;
}
